import { Injectable, inject } from '@angular/core';
import { GraphQLClientService } from '../../../../../../core/services/graphql-client.service';
import { FeatureFlagsViewerService } from '../../../../../../core/services/feature-flags-viewer.service';
import { isErr, ok } from '../../../../../../core/models/api-result';
import type { ApiResult } from '../../../../../../core/models/api-result';
import type { CmdkItem, CmdkNewsItem, CmdkNewsType } from '../../models/cmdk.model';
import type { CmdkProviderContext } from '../../models/cmdk-provider';
import { CmdkRemoteProviderBase } from './remote.provider.base';
import { contentPictureUrl } from '../../../../../../shared/media/content-picture';
import { OrganisationVideosService } from '../../../../../videos/organisation-videos.service';

/**
 * RESPONSIBILITY: the "News & docs" scope — published articles plus knowledge
 * pages, which are two different backends presented as one group because to
 * the person searching they are the same thing: something written down.
 *
 * BACKEND CONTRACT: two calls in parallel.
 *
 *   searchArticles        a document declared here. `NewsApiService`'s own
 *                         `searchArticles()` is stateless and was reused at
 *                         first, but it selects a whole feed card including
 *                         `audioSummaryUrl` — an async per-row URL signer. See
 *                         the note above `CMDK_SEARCH_ARTICLES`.
 *   searchKnowledgePages  a document declared here. `KnowledgeApiService`'s
 *                         own version is not reusable: it goes through the
 *                         DEPRECATED `query<T>` and does `?? []`, so a failure
 *                         is indistinguishable from no matches and the group
 *                         could never render its error banner.
 *
 * Two calls rather than one multi-root document: under a single document a
 * field error on either root fails both, and one dead half must not blank the
 * group. An error is returned only when BOTH halves fail; if one succeeds the
 * group renders what it has and the other is logged.
 *
 * AI CONTEXT — the two fields never to select
 *
 * `audioSummaryUrl` is an async resolver that signs a URL PER ROW, and
 * `createdBy { picture }` is async too. Either turns one search into N
 * round-trips inside the backend. `readTimeMinutes` is free — a sync
 * computation over an already-loaded body — and does not put `body` on the
 * wire.
 *
 * `path` is deliberately absent from the knowledge selection: the backend's
 * own `.select()` never loads it and the field is nullable, so asking for it
 * returns null and looks like missing data rather than an unasked question.
 */
/**
 * The palette's OWN article document.
 *
 * `NewsApiService.searchArticles()` is stateless and was the obvious thing to
 * reuse — but it selects `ARTICLE_CARD_FIELDS`, which is a whole feed card:
 * `audioSummaryUrl`, `viewCount`, `likeCount`, `isLiked`, `isRead`,
 * `shareCount`, `commentCount`, `coverImage`, `divisionId`, `createdBy`.
 *
 * `audioSummaryUrl` is the one that matters. It is an async `@ResolveField`
 * that calls `signedUrlService.signUrl` PER ARTICLE
 * (rdx-platform-api articles.resolver.ts:362) — so an eight-result search is
 * eight URL signings inside the backend, on every debounced keystroke, for a
 * field the palette never renders. Verified against the running API.
 *
 * So the palette asks for the seven fields it actually uses. `readTimeMinutes`
 * is kept because it is free: a synchronous word count over an already-loaded
 * body, and asking for it does not put `body` on the wire.
 *
 * `mediaType` and `urgency` decide the row glyph. `mediaType` does NOT exist on
 * the GraphQL type — `NewsApiService` derives it from `contentType`
 * (news-api.service.ts:150), and that one line is reproduced in `newsTypeFor`
 * below rather than importing a private mapper.
 */
const CMDK_SEARCH_ARTICLES = `
  query CmdkSearchArticles($query: String!, $limit: Int) {
    searchArticles(query: $query, limit: $limit) {
      _id
      title
      description
      contentType
      urgency
      source
      readTimeMinutes
      coverImage
      media { image { url } video { posterUrl } }
      kind
      spotlight { needsYou }
    }
  }
`;

interface CmdkArticleDto {
  _id: string;
  title: string;
  description?: string;
  contentType?: string;
  urgency?: string;
  source?: string;
  readTimeMinutes?: number;
  coverImage?: string;
  /**
   * The SIGNED structured media set. `searchArticles` returns `ArticleType`,
   * so the same `media` resolver applies here as everywhere else — and the
   * same reason applies for reading it: `coverImage` is unsigned and empty on
   * every seeded row. See `shared/media/content-picture.ts`.
   */
  media?: {
    image?: { url?: string | null } | null;
    video?: { posterUrl?: string | null } | null;
  } | null;
  /** `ArticleType.kind`: POST, SPOTLIGHT, or null on a legacy row. */
  kind?: string | null;
  /** `ArticleType.spotlight`: non-null when the article has widgets. */
  spotlight?: { needsYou?: boolean | null } | null;
}

const CMDK_SEARCH_KNOWLEDGE_PAGES = `
  query CmdkSearchKnowledgePages($query: String!, $limit: Int) {
    searchKnowledgePages(query: $query, limit: $limit) {
      _id
      title
      icon
      workspaceId
      updatedAt
    }
  }
`;

interface CmdkPageDto {
  _id: string;
  title: string;
  icon?: string;
  workspaceId?: string;
  updatedAt?: string;
}

const ARTICLE_LIMIT = 8;
const PAGE_LIMIT = 5;

@Injectable({ providedIn: 'root' })
export class CmdkNewsProvider extends CmdkRemoteProviderBase {
  private readonly graphql = inject(GraphQLClientService);
  private readonly flags = inject(FeatureFlagsViewerService);
  private readonly videos = inject(OrganisationVideosService);

  readonly id = 'news';
  readonly kind = 'news' as const;
  readonly scope = 'news' as const;
  readonly minQueryLength = 3;

  /**
   * Articles and knowledge pages carry `@FeatureFlag('content.viewer')`.
   * Organisation videos do not — the library is ungated — so the News group
   * stays on for every signed-in viewer and the article/page halves opt in
   * inside `fetch`.
   */
  isEnabled(): boolean {
    return true;
  }

  protected async fetch(ctx: CmdkProviderContext): Promise<ApiResult<CmdkItem[]>> {
    // The knowledge half is gated a second time, more narrowly: the only route
    // that can render a page lives under `/flow`, so on a tenant without it
    // every page row would be a link to nowhere.
    const wantArticles = this.flags.is('content.viewer');
    const wantPages = wantArticles && this.flags.is('flow');

    const [articles, pages, films] = await Promise.all([
      wantArticles
        ? this.searchArticles(ctx.query)
        : Promise.resolve(ok<CmdkArticleDto[]>([])),
      wantPages ? this.searchPages(ctx.query) : Promise.resolve(ok<CmdkPageDto[]>([])),
      this.searchVideos(ctx.query),
    ]);

    const articlesFailed = isErr(articles);
    const pagesFailed = isErr(pages);
    const filmsFailed = isErr(films);

    // Only a total failure is an error. One half down still has something
    // worth showing, and blanking the group would hide it.
    if (articlesFailed && pagesFailed) return articles;
    if (articlesFailed) {
      console.warn('[CmdkNewsProvider] article search failed:', articles.error.message);
    }
    if (pagesFailed) {
      console.warn('[CmdkNewsProvider] knowledge search failed:', pages.error.message);
    }

    const items: CmdkNewsItem[] = [
      ...(filmsFailed ? [] : films.data),
      ...(articlesFailed ? [] : articles.data.map(toArticleItem)),
      ...(pagesFailed ? [] : pages.data.map(toPageItem)),
    ];
    return ok(items);
  }

  private async searchArticles(query: string): Promise<ApiResult<CmdkArticleDto[]>> {
    const result = await this.graphql.queryResult<CmdkArticleDto[]>(
      CMDK_SEARCH_ARTICLES,
      { query, limit: ARTICLE_LIMIT },
      'network-only',
    );
    return isErr(result) ? result : ok(result.data ?? []);
  }

  private async searchPages(query: string): Promise<ApiResult<CmdkPageDto[]>> {
    const result = await this.graphql.queryResult<CmdkPageDto[]>(
      CMDK_SEARCH_KNOWLEDGE_PAGES,
      { query, limit: PAGE_LIMIT },
      'network-only',
    );
    // `queryResult` types data as nullable; a null payload for a list field is
    // "no rows", not a failure, and collapsing it here keeps the merge below
    // free of a second null check.
    return isErr(result) ? result : ok(result.data ?? []);
  }

  private async searchVideos(query: string): Promise<ApiResult<CmdkNewsItem[]>> {
    try {
      await this.videos.ensureLoaded();
      const needle = query.trim().toLowerCase();
      const rows = needle
        ? this.videos.all().filter((row) => row.title.toLowerCase().includes(needle))
        : this.videos.all();
      return ok(rows.slice(0, 8).map(toVideoItem));
    } catch {
      return ok([]);
    }
  }
}

/**
 * A Spotlight, for the palette's own group (Spotlight PLAN 7.4): written as
 * one (`kind: SPOTLIGHT`), or a legacy article that carries widgets. A
 * legacy article with no widgets stays news — the palette does not rename the
 * whole back catalogue.
 */
export function isSpotlightArticle(article: Pick<CmdkArticleDto, 'kind' | 'spotlight'>): boolean {
  return (article.kind ?? '').toUpperCase() === 'SPOTLIGHT' || !!article.spotlight;
}

function toArticleItem(article: CmdkArticleDto): CmdkNewsItem {
  if (isSpotlightArticle(article)) {
    return {
      id: article._id,
      kind: 'news',
      group: 'spotlights',
      title: article.title,
      subtitle: joinMeta(article),
      newsType: 'spotlight',
      section: article.source,
      readTimeMinutes: article.readTimeMinutes,
      imageUrl: contentPictureUrl(article),
      summary: article.description,
    };
  }
  return {
    id: article._id,
    kind: 'news',
    title: article.title,
    subtitle: joinMeta(article),
    newsType: newsTypeFor(article),
    section: article.source,
    readTimeMinutes: article.readTimeMinutes,
    imageUrl: contentPictureUrl(article),
    summary: article.description,
  };
}

function toVideoItem(clip: {
  id: string;
  title: string;
  source: 'ARTICLE' | 'EVENT';
  posterUrl?: string | null;
}): CmdkNewsItem {
  return {
    id: clip.id,
    kind: 'news',
    title: clip.title,
    subtitle: clip.source === 'EVENT' ? 'Event' : 'Post',
    newsType: 'video',
    imageUrl: clip.posterUrl ?? undefined,
  };
}

function toPageItem(page: CmdkPageDto): CmdkNewsItem {
  return {
    id: page._id,
    kind: 'news',
    title: page.title,
    subtitle: undefined,
    newsType: 'page',
    // Carried so the row can be opened; deliberately NOT `section`, which is
    // rendered — a raw workspace id is not a place name.
    workspaceId: page.workspaceId,
  };
}

/**
 * `mediaType` distinguishes video from article; `urgency` is what makes an
 * article an announcement. Both come off the same record, so the row can show
 * the right glyph without a second request.
 */
function newsTypeFor(article: CmdkArticleDto): CmdkNewsType {
  // `mediaType` is not a GraphQL field — NewsApiService derives it from
  // `contentType` the same way (news-api.service.ts:150).
  if ((article.contentType ?? '').toLowerCase() === 'video') return 'video';
  if (article.urgency) return 'announcement';
  return 'article';
}

function joinMeta(article: CmdkArticleDto): string | undefined {
  const parts = [article.contentType, article.source].filter((p): p is string => !!p);
  return parts.length ? parts.join(' · ') : undefined;
}
